import type { EventSchedule } from '../lib/types';

export function currencySymbol(currency?: string | null): string {
  const code = (currency ?? 'eur').toUpperCase();
  if (code === 'EUR') return '€';
  if (code === 'USD') return '$';
  if (code === 'GBP') return '£';
  return `${code} `;
}

export function formatPrice(amount: number, currency?: string | null): string {
  const value = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${currencySymbol(currency)}${value}`;
}

const dayFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short'
});

const timeFormat = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});

function parseDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Sat 12 Jul, 21:00 – 04:00" (same night) or "Sat 12 Jul 21:00 – Sun 13 Jul 04:00". */
export function formatEventDate(schedule?: EventSchedule | null): string | null {
  const start = parseDate(schedule?.start);
  if (!start) return null;
  const end = parseDate(schedule?.end);
  const startDay = dayFormat.format(start);
  const startTime = timeFormat.format(start);
  if (!end) return `${startDay}, ${startTime}`;
  const sameDay = end.getTime() - start.getTime() < 24 * 60 * 60 * 1000;
  if (sameDay) {
    return `${startDay}, ${startTime} – ${timeFormat.format(end)}`;
  }
  return `${startDay} ${startTime} – ${dayFormat.format(end)} ${timeFormat.format(end)}`;
}

/**
 * '#ff4d17' → 'ff4d17' parsed into '255 77 23' for the --accent-rgb custom
 * property. Returns null for anything that isn't a #rrggbb hex, so an
 * invalid stored color silently falls back to the Gravity default.
 */
export function accentChannels(hex?: string | null): string | null {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const value = parseInt(hex.slice(1), 16);
  return `${(value >> 16) & 255} ${(value >> 8) & 255} ${value & 255}`;
}

/** Inline style that rethemes a page subtree to a community's accent color. */
export function accentStyle(themeColor?: string | null): string {
  const channels = accentChannels(themeColor);
  return channels ? `--accent-rgb: ${channels};` : '';
}

export function isUpcoming(schedule?: EventSchedule | null): boolean {
  const end = parseDate(schedule?.end) ?? parseDate(schedule?.start);
  if (!end) return true;
  return end.getTime() >= Date.now() - 6 * 60 * 60 * 1000;
}

/* ---------------------------------------------------------------------------
 * Day grouping for the discovery catalog (Shotgun-style day columns).
 * All keying is in the browser's local timezone, matching the formatters
 * above — a "day" is a local calendar day, not a UTC one.
 * ------------------------------------------------------------------------- */

const dayHeadingParts = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short'
});

const dayHeadingPartsWithYear = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric'
});

const monthDayFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short'
});

/** Local YYYY-MM-DD key for a date — stable across timezones for grouping. */
function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** A group of events sharing one local calendar day, keyed by that day. */
export interface EventDayGroup<T> {
  /** Local YYYY-MM-DD of the day. */
  key: string;
  /** Local midnight of the day, for label formatting. */
  date: Date;
  /** True when the group is today's — label reads "Tonight". */
  isToday: boolean;
  events: T[];
}

/**
 * A genuine multi-day run (festival-style), not a club night that merely spills
 * past midnight. True only when the end lands on a later calendar day AND the
 * span is at least a full day — mirroring formatEventDate's "same night" cutoff.
 */
export function isMultiDay(schedule?: EventSchedule | null): boolean {
  const start = parseDate(schedule?.start);
  const end = parseDate(schedule?.end);
  if (!start || !end) return false;
  if (localDayKey(end) === localDayKey(start)) return false;
  return end.getTime() - start.getTime() >= 24 * 60 * 60 * 1000;
}

/**
 * Group upcoming events into local-day buckets, days ascending and events
 * within a day by start time. A multi-day run that has already started but is
 * still ongoing (start day is in the past, end is still ahead) surfaces under
 * TODAY at the top rather than under its vanished start day.
 */
export function groupEventsByDay<T extends { schedule?: EventSchedule | null }>(
  events: T[],
  now: Date = new Date()
): EventDayGroup<T>[] {
  const todayKey = localDayKey(now);
  const buckets = new Map<string, EventDayGroup<T>>();

  const ensure = (key: string, date: Date): EventDayGroup<T> => {
    let group = buckets.get(key);
    if (!group) {
      group = { key, date, isToday: key === todayKey, events: [] };
      buckets.set(key, group);
    }
    return group;
  };

  for (const event of events) {
    const start = parseDate(event.schedule?.start);
    if (!start) continue;
    const end = parseDate(event.schedule?.end);
    // Ongoing multi-day run (started before today, not yet over) → pin to today.
    const ongoing = end != null && start.getTime() < now.getTime() && end.getTime() >= now.getTime();
    const bucketDate = ongoing
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
      : new Date(start.getFullYear(), start.getMonth(), start.getDate());
    ensure(localDayKey(bucketDate), bucketDate).events.push(event);
  }

  const time = (event: T) => {
    const value = new Date(event.schedule?.start ?? '').getTime();
    return Number.isNaN(value) ? Number.MAX_SAFE_INTEGER : value;
  };

  return [...buckets.values()]
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map(group => ({ ...group, events: [...group.events].sort((a, b) => time(a) - time(b)) }));
}

/**
 * Day-group heading text. Today's group reads "Tonight"; every other day is
 * "Tue 7 Jul", with the year appended only when it is not the current year.
 * Returned in natural case — the day-header CSS uppercases it.
 */
export function formatDayHeading(day: Date, now: Date = new Date()): string {
  if (localDayKey(day) === localDayKey(now)) return 'Tonight';
  const fmt = day.getFullYear() === now.getFullYear() ? dayHeadingParts : dayHeadingPartsWithYear;
  return fmt.format(day);
}

/**
 * Card meta line: a single-day event reads "Sat 12 Jul, 21:00"; a multi-day
 * run collapses to a date range ("7 Jul – 11 Jul") with no times.
 */
export function formatEventMeta(schedule?: EventSchedule | null): string | null {
  const start = parseDate(schedule?.start);
  if (!start) return null;
  if (isMultiDay(schedule)) {
    const end = parseDate(schedule?.end);
    if (end) return `${monthDayFormat.format(start)} – ${monthDayFormat.format(end)}`;
  }
  return `${dayFormat.format(start)}, ${timeFormat.format(start)}`;
}

/* ---------------------------------------------------------------------------
 * Card call-to-action mapping.
 *
 * Every card/row CTA reads off the viewer's standing (server-computed per
 * event). The CTA is a pure AFFORDANCE: whatever it says, clicking it only
 * navigates to the standing-aware event page (/e/<id>) — the event page owns
 * the real claim/apply/buy flows. So this helper never encodes a second
 * checkout path; it only names what awaits and how loudly to say it.
 *
 *   kind 'button' → a real <button> (accent / success / ghost / disabled)
 *   kind 'text'   → the quiet muted footer text for pure wait states
 * ------------------------------------------------------------------------- */

export type CardActionTone = 'accent' | 'success' | 'ghost' | 'disabled' | 'muted';

export interface CardAction {
  label: string;
  tone: CardActionTone;
  kind: 'button' | 'text';
  /** Only meaningful for kind 'button'; sets the disabled attribute. */
  disabled: boolean;
  /** Render the accent-toned "Invited" status chip next to the community chip. */
  invitedChip: boolean;
}

/** The event facts the CTA mapping needs, page-agnostic. */
export interface CardActionInput {
  standing?: string | null;
  soldOut?: boolean | null;
  ticketPrice?: number | null;
  applicationUrl?: string | null;
  /** True when a community apply-to-join path exists (visibility !== 'hidden'). */
  hasCommunityApplyPath?: boolean;
  /** Viewer already holds a non-cancelled ticket — always wins as "going". */
  owned?: boolean;
}

const btn = (
  label: string,
  tone: CardActionTone,
  opts: { disabled?: boolean; invitedChip?: boolean } = {}
): CardAction => ({
  label,
  tone,
  kind: 'button',
  disabled: opts.disabled ?? false,
  invitedChip: opts.invitedChip ?? false
});

const txt = (label: string): CardAction => ({
  label,
  tone: 'muted',
  kind: 'text',
  disabled: false,
  invitedChip: false
});

/**
 * Map a viewer's standing toward an event to its card CTA. The button variants
 * navigate to /e/<id>; the two 'text' variants are quiet wait states that stay
 * as muted footer text (no button). See the state→action table this encodes:
 * canBuy/invited buy, stranger applies, going/manager/unknown each get a button.
 */
export function cardAction(input: CardActionInput): CardAction {
  // A held ticket always wins over standing — belt-and-suspenders for 'going'.
  if (input.owned || input.standing === 'going') {
    return btn("You're going", 'success');
  }
  switch (input.standing) {
    case 'manager':
      return btn('Manage event', 'ghost');
    case 'canBuy':
      if (input.soldOut) return btn('Sold out', 'disabled', { disabled: true });
      return btn(input.ticketPrice === 0 ? 'Get ticket' : 'Buy ticket', 'accent');
    case 'invited':
      // An invite IS permission to buy (claimed at purchase) — same button
      // treatment as canBuy, plus the pre-click "Invited" status chip.
      if (input.soldOut) return btn('Sold out', 'disabled', { disabled: true, invitedChip: true });
      return btn(input.ticketPrice === 0 ? 'Get ticket' : 'Buy ticket', 'accent', {
        invitedChip: true
      });
    case 'applicationPending':
      return txt('Application pending');
    case 'stranger':
      // Mirror EventPage's apply-path: community flow → external URL → invite-only.
      if (input.hasCommunityApplyPath) return btn('Apply to join', 'accent');
      if (input.applicationUrl) return btn('Apply', 'accent');
      return txt('By invitation');
    default:
      // Missing/unknown standing: a neutral affordance into the event page.
      return btn('View event', 'ghost');
  }
}
